import { memo, useState, useEffect } from "react";
import Form from "../../components/Form.js";
import Table from "../../components/Table.js";
import SpreadsheetEditor from "../../components/SpreadsheetEditor.js";
import { useSnackbar } from "../../utils/index.js";
import Spinner from "../../components/Spinner.js";
import { Box, Typography } from "@mui/material";
import useSelectStyles from "../../components/styles/Select.js";
import {
    reportTitleHeader,
    reportDateHeader,
} from "../../components/TableHeaders.js";
import {
    getAllProblemReports,
    createProblemReport,
    updateProblemReport,
    deleteProblemReport,
} from "../../api/index.js";
import AreYouSurePopup from "../../components/popups/AreYouSurePopup.js";

const ProblemsReport = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [fetchedReports, setFetchedReports] = useState([]);
    const [step, setStep] = useState(1);
    const [selectedReport, setSelectedReport] = useState(null);
    const [currentTitle, setCurrentTitle] = useState("");
    const [createdReportId, setCreatedReportId] = useState(null);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);

    const { error, success } = useSnackbar();
    const selectClasses = useSelectStyles();

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const { success: scs, reports, message } = await getAllProblemReports();
            if (scs) {
                setFetchedReports(reports);
            } else {
                error(message);
            }
        } catch (err) {
            error("Σφάλμα κατά τη φόρτωση των αναφορών");
        }
        setIsLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, []);

    const formContent = [
        {
            customType: "input",
            id: "title",
            type: "text",
            placeholder: "Τίτλος",
            required: true,
            helperText:
                "Εισάγετε έναν τίτλο με τον οποίο θα μπορείτε να αναγνωρίζετε την καταγραφή",
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Δημιουργία",
        },
    ];

    const createReport = async (values) => {
        setIsLoading(true);
        setCurrentTitle(values.title);
        const { success: scs, reportId, message } = await createProblemReport(values.title);
        if (scs) {
            const reportTitleObj = {
                title: values.title,
            }
            setCreatedReportId(reportId);
            setSelectedReport(reportTitleObj);
            setStep(2);
        } else {
            error(message);
        }
        setIsLoading(false);
    };

    const handleSaveReport = async (spreadsheetData) => {
        setIsLoading(true);
        try {
            const reportId = createdReportId || selectedReport?._id;

            const { success: scs, message } = await updateProblemReport(reportId, spreadsheetData);
            if (scs) {
                success(message);
                await fetchData();
            } else {
                error(message);
            }
        } catch (err) {
            error("Σφάλμα κατά την αποθήκευση");
        }
        setIsLoading(false);
    };

    const handleDeleteReport = async () => {
        const reportId = createdReportId || selectedReport?._id;

        if (!reportId) {
            error("Δεν μπορεί να διαγραφεί μια μη αποθηκευμένη αναφορά");
            return;
        }

        setIsLoading(true);
        try {
            const { success: scs, message } = await deleteProblemReport(reportId);

            if (scs) {
                success(message);
                setStep(1);
                setSelectedReport(null);
                setCreatedReportId(null);
                await fetchData();
            } else {
                error(message);
            }
        } catch (err) {
            error("Σφάλμα κατά τη διαγραφή");
        }
        setIsDeletePopupOpen(false);
        setIsLoading(false);
    };

    const handleBackToList = () => {
        setStep(1);
        fetchData();
        setSelectedReport(null);
        setCreatedReportId(null);
    };

    const handleReportClick = (value) => {
        const report = fetchedReports.find((report) => report.title === value);
        setSelectedReport(report);
        setCreatedReportId(report._id);
        setCurrentTitle(report.title);
        setStep(2);
    };

    const reportTableColumns = [
        reportTitleHeader(handleReportClick),
        reportDateHeader,
    ];

    return (
        <>
            <Spinner open={isLoading} />
            <AreYouSurePopup
                width={"900px"}
                isOpen={isDeletePopupOpen}
                title="Διαγραφή Αναφοράς"
                titleColor="black"
                onClose={() => setIsDeletePopupOpen(false)}
                innerText={`Είστε σίγουροι ότι θέλετε να διαγράψετε την Αναφορά "${selectedReport?.title}";`}
                declineText="Πίσω"
                onDecline={() => setIsDeletePopupOpen(false)}
                acceptText="Ναι, Διαγραφή Αναφοράς"
                onAccept={() => handleDeleteReport()}
            />
            {step === 1 && (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            width: "100%",
                            paddingTop: "2rem",
                        }}
                    >
                        <Box flexDirection="column" className={selectClasses.innerBox}>
                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                {"Δημιουργία Νέας Καταγραφής"}
                            </Typography>
                            <Form content={formContent} onSubmit={createReport} />
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            width: "100%",
                            paddingTop: "2rem",
                        }}
                    >
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            {"Λίστα Προηγούμενων Ελέγχων"}
                        </Typography>
                        <Box sx={{ width: "100%", maxWidth: "1200px", padding: "0 1rem" }}>
                            <Table
                                data={fetchedReports}
                                noDataText={
                                    isLoading
                                        ? "Φόρτωση..."
                                        : "Δεν υπάρχουν καταγεγραμμένοι έλεγχοι"
                                }
                                columns={reportTableColumns}
                                showPageSizeOptions={true}
                            />
                        </Box>
                    </Box>
                </>
            )}

            {step === 2 && (
                <SpreadsheetEditor
                    title={currentTitle}
                    data={selectedReport?.data}
                    onSave={handleSaveReport}
                    onBack={handleBackToList}
                    onDelete={(createdReportId || selectedReport?._id) ? () => setIsDeletePopupOpen(true) : null}
                />
            )}
        </>
    );
};

export default memo(ProblemsReport);